import React from "react";
import {
  App,
  Button,
  Card,
  Empty,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Tooltip,
} from "antd";
import {
  FolderOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, fetchT, centsToDollar } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import {
  ItemSchemaOption,
  PrepaidCardSchema,
  PrepaidCardSchemaRequest,
  PrepaidCardStatsResponse,
  daysOfWeekMaskToIndices,
  getPrepaidCardSchemaStatus,
} from "./types";
import { PrepaidCardForm } from "./Form";

const { Text } = Typography;

type PrepaidCardEditorProps = {
  restaurantId: string;
};

type ItemSchemaGroupResponse = {
  item_schema_groups: Array<{
    item_schemas: Array<{ id: string; name: string }>;
  }>;
};

const DAY_ABBR_KEYS = [
  "day_sun_abbr",
  "day_mon_abbr",
  "day_tue_abbr",
  "day_wed_abbr",
  "day_thu_abbr",
  "day_fri_abbr",
  "day_sat_abbr",
];

export const PrepaidCardEditor: React.FC<PrepaidCardEditorProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { createSuccess, updateSuccess, showSuccess, notifyError } = useNotification();
  const { modal } = App.useApp();

  const [schemas, setSchemas] = React.useState<PrepaidCardSchema[] | undefined>(undefined);
  const [cardsSoldBySchema, setCardsSoldBySchema] = React.useState<Record<string, number>>({});
  const [itemSchemaOptions, setItemSchemaOptions] = React.useState<ItemSchemaOption[]>([]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingSchema, setEditingSchema] = React.useState<PrepaidCardSchema | undefined>(
    undefined
  );
  const [submitLoading, setSubmitLoading] = React.useState(false);

  const refreshSchemas = () => {
    getFetchT<PrepaidCardSchema[]>(
      `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas?include_archived=true`,
      (result) => setSchemas(result ?? []),
      (err) => {
        notifyError(t("failed_to_fetch_prepaid_cards"), err);
        setSchemas([]);
      }
    );
  };

  const refreshCardsSold = () => {
    // Cards sold isn't part of the schema list response; pull it from the
    // stats endpoint over an all-time window, mirroring the check the
    // backend itself does before allowing a draft delete.
    const start = new Date(0).toISOString();
    const end = dayjs().add(100, "years").toISOString();
    getFetchT<PrepaidCardStatsResponse>(
      `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_stats?start_time=${encodeURIComponent(
        start
      )}&end_time=${encodeURIComponent(end)}`,
      (result) => {
        const map: Record<string, number> = {};
        for (const s of result.stats ?? []) {
          map[s.prepaid_card_schema_id] = s.cards_sold;
        }
        setCardsSoldBySchema(map);
      },
      () => {
        // Non-critical: the list still renders without the sold count.
      }
    );
  };

  const refreshItemSchemas = () => {
    // Prepaid cards are redeemed by dine-in customers ordering off the
    // regular menu, so only `dine_in` items are offerable as supported
    // items -- `large_volume` (catering) items are never redeemable and
    // would just be noise in the picker.
    getFetchT<ItemSchemaGroupResponse>(
      `${API_URL}/v1/r/${restaurantId}/menu?menu_type=dine_in`,
      (data) => {
        const allItems = data.item_schema_groups.flatMap((g) => g.item_schemas);
        setItemSchemaOptions(allItems);
      },
      (err) => {
        notifyError(t("failed_to_fetch_item_schemas"), err);
      }
    );
  };

  React.useEffect(() => {
    refreshSchemas();
    refreshCardsSold();
    refreshItemSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const openCreate = () => {
    setEditingSchema(undefined);
    setModalOpen(true);
  };

  const openEdit = (schema: PrepaidCardSchema) => {
    setEditingSchema(schema);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingSchema(undefined);
  };

  const handleSubmit = (req: PrepaidCardSchemaRequest) => {
    setSubmitLoading(true);
    if (editingSchema) {
      fetchT<PrepaidCardSchema>(
        "PUT",
        `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas/${editingSchema.id}`,
        req,
        () => {
          updateSuccess(t("prepaid_card"));
          setSubmitLoading(false);
          closeModal();
          refreshSchemas();
        },
        (err) => {
          notifyError(t("failed_to_update_prepaid_card"), err);
          setSubmitLoading(false);
        }
      );
    } else {
      fetchT<PrepaidCardSchema>(
        "POST",
        `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas`,
        req,
        () => {
          createSuccess(t("prepaid_card"));
          setSubmitLoading(false);
          closeModal();
          refreshSchemas();
        },
        (err) => {
          notifyError(t("failed_to_create_prepaid_card"), err);
          setSubmitLoading(false);
        }
      );
    }
  };

  const handlePublish = (schema: PrepaidCardSchema) => {
    const dayLabels = DAY_ABBR_KEYS.map((k) => t(k));
    const allowedDays = daysOfWeekMaskToIndices(schema.redeem_days_of_week)
      .map((d) => dayLabels[d])
      .join(", ");

    modal.confirm({
      title: t("prepaid_card_publish_confirm_title", { name: schema.name }),
      icon: <ExclamationCircleOutlined style={{ color: token.colorError }} />,
      okText: t("publish"),
      okType: "danger",
      cancelText: t("cancel"),
      width: 560,
      content: (
        <div>
          <p>{t("prepaid_card_publish_warning")}</p>
          <p style={{ marginBottom: 0 }}>
            <Text strong>
              ${centsToDollar(schema.price)} · {schema.total_redeem_count}{" "}
              {t("prepaid_card_redeems_lowercase")} · {allowedDays}
            </Text>
            <br />
            <Text type="secondary">
              {t("prepaid_card_redeem_window")}:{" "}
              {dayjs(schema.redeem_start_time).format("MMM D, YYYY")} –{" "}
              {dayjs(schema.redeem_end_time).format("MMM D, YYYY")}
            </Text>
            <br />
            <Text type="secondary">
              {t("prepaid_card_purchase_window")}:{" "}
              {dayjs(schema.purchase_start_time).format("MMM D, YYYY")} –{" "}
              {dayjs(schema.purchase_end_time).format("MMM D, YYYY")}
            </Text>
          </p>
        </div>
      ),
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          fetchT<PrepaidCardSchema>(
            "POST",
            `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas/${schema.id}/publish`,
            {},
            () => {
              showSuccess(t("prepaid_card_published_successfully"));
              refreshSchemas();
              resolve();
            },
            (err) => {
              notifyError(t("failed_to_publish_prepaid_card"), err);
              reject(err);
            }
          );
        }),
    });
  };

  const handleArchive = (schema: PrepaidCardSchema) => {
    modal.confirm({
      title: t("prepaid_card_archive_confirm_title", { name: schema.name }),
      content: t("prepaid_card_archive_confirm_body"),
      okText: t("archive"),
      cancelText: t("cancel"),
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          fetchT<PrepaidCardSchema>(
            "POST",
            `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas/${schema.id}/archive`,
            {},
            () => {
              showSuccess(t("prepaid_card_archived_successfully"));
              refreshSchemas();
              resolve();
            },
            (err) => {
              notifyError(t("failed_to_archive_prepaid_card"), err);
              reject(err);
            }
          );
        }),
    });
  };

  const handleDelete = (schema: PrepaidCardSchema) => {
    modal.confirm({
      title: t("prepaid_card_delete_confirm_title", { name: schema.name }),
      content: t("prepaid_card_delete_confirm_body"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          fetchT<{}>(
            "DELETE",
            `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas/${schema.id}`,
            {},
            () => {
              showSuccess(t("prepaid_card_deleted_successfully"));
              refreshSchemas();
              resolve();
            },
            (err) => {
              notifyError(t("failed_to_delete_prepaid_card"), err);
              reject(err);
            }
          );
        }),
    });
  };

  const statusTag = (schema: PrepaidCardSchema) => {
    const status = getPrepaidCardSchemaStatus(schema);
    const colorByStatus: Record<string, string> = {
      DRAFT: "default",
      SCHEDULED: "gold",
      LIVE: "green",
      ENDED: "default",
      ARCHIVED: "red",
    };
    const labelByStatus: Record<string, string> = {
      DRAFT: t("prepaid_card_status_draft"),
      SCHEDULED: t("prepaid_card_status_scheduled"),
      LIVE: t("prepaid_card_status_live"),
      ENDED: t("prepaid_card_status_ended"),
      ARCHIVED: t("prepaid_card_status_archived"),
    };
    return <Tag color={colorByStatus[status]}>{labelByStatus[status]}</Tag>;
  };

  const dayChips = (mask: number) => {
    const dayLabels = DAY_ABBR_KEYS.map((k) => t(k));
    const selected = new Set(daysOfWeekMaskToIndices(mask));
    return (
      <Space size={4} wrap>
        {dayLabels.map((label, idx) => (
          <Tag
            key={idx}
            style={{
              margin: 0,
              opacity: selected.has(idx) ? 1 : 0.35,
              background: selected.has(idx) ? token.colorPrimaryBg : undefined,
            }}
          >
            {label}
          </Tag>
        ))}
      </Space>
    );
  };

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, schema: PrepaidCardSchema) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {statusTag(schema)}
        </Space>
      ),
    },
    {
      title: t("prepaid_card_price"),
      key: "price",
      render: (_: unknown, schema: PrepaidCardSchema) => `$${centsToDollar(schema.price)}`,
    },
    {
      title: t("prepaid_card_redeem_count"),
      dataIndex: "total_redeem_count",
      key: "total_redeem_count",
    },
    {
      title: t("prepaid_card_purchase_window"),
      key: "purchase_window",
      render: (_: unknown, schema: PrepaidCardSchema) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: token.fontSizeSM }}>
            {dayjs(schema.purchase_start_time).format("YYYY-MM-DD")} –{" "}
            {dayjs(schema.purchase_end_time).format("YYYY-MM-DD")}
          </Text>
        </Space>
      ),
    },
    {
      title: t("prepaid_card_redeem_window"),
      key: "redeem_window",
      render: (_: unknown, schema: PrepaidCardSchema) => (
        <Space direction="vertical" size={4}>
          <Text style={{ fontSize: token.fontSizeSM }}>
            {dayjs(schema.redeem_start_time).format("YYYY-MM-DD")} –{" "}
            {dayjs(schema.redeem_end_time).format("YYYY-MM-DD")}
          </Text>
          {dayChips(schema.redeem_days_of_week)}
        </Space>
      ),
    },
    {
      title: t("prepaid_card_cards_sold"),
      key: "cards_sold",
      render: (_: unknown, schema: PrepaidCardSchema) => cardsSoldBySchema[schema.id] ?? 0,
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, schema: PrepaidCardSchema) => {
        const status = getPrepaidCardSchemaStatus(schema);
        const isDraft = status === "DRAFT";
        const isArchivable = !isDraft && status !== "ARCHIVED";
        const cardsSold = cardsSoldBySchema[schema.id] ?? 0;
        return (
          <Space wrap>
            {isDraft && (
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(schema)}>
                {t("edit")}
              </Button>
            )}
            {!isDraft && (
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(schema)}>
                {t("view")}
              </Button>
            )}
            {isDraft && (
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                onClick={() => handlePublish(schema)}
              >
                {t("publish")}
              </Button>
            )}
            {isArchivable && (
              <Button size="small" icon={<FolderOutlined />} onClick={() => handleArchive(schema)}>
                {t("archive")}
              </Button>
            )}
            {isDraft &&
              (cardsSold > 0 ? (
                <Tooltip title={t("prepaid_card_delete_disabled_sold")}>
                  <Button size="small" danger icon={<DeleteOutlined />} disabled>
                    {t("delete")}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(schema)}
                >
                  {t("delete")}
                </Button>
              ))}
          </Space>
        );
      },
    },
  ];

  if (schemas === undefined) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <CreditCardOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("prepaid_cards")}
            </Text>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t("prepaid_card_create")}
          </Button>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {schemas.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("prepaid_card_none_available")}</Text>}
          />
        ) : (
          <Table
            dataSource={schemas}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      <Modal
        title={
          editingSchema
            ? getPrepaidCardSchemaStatus(editingSchema) === "DRAFT"
              ? t("prepaid_card_edit")
              : t("prepaid_card_view")
            : t("prepaid_card_create")
        }
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={720}
        destroyOnClose
      >
        <PrepaidCardForm
          restaurantId={restaurantId}
          schema={editingSchema}
          itemSchemaOptions={itemSchemaOptions}
          loading={submitLoading}
          onSubmit={handleSubmit}
          onCancel={closeModal}
        />
      </Modal>
    </div>
  );
};
